"use client";

import { useEffect } from "react";
import { sendClientErrorReport } from "@/lib/client-error-report";

/**
 * O que o Next.js entrega para uma Error Boundary. Os nomes seguem a API do
 * framework de propósito: `error` e `errorInfo` são as props de `app/error.tsx`.
 */
interface ErrorBoundaryLike {
  error: Error;
  errorInfo?: { componentStack?: string | null } | null;
  /** `digest` do Next.js: liga o alerta do browser ao log do servidor. */
  digest?: string;
}

interface HookOptions extends ErrorBoundaryLike {
  context?: Record<string, string | number | boolean | null | undefined>;
}

/**
 * Reporta erros capturados por uma Error Boundary.
 *
 * `useEffect` (e não o corpo do hook) porque o report precisa acontecer depois
 * do commit, quando `error` já está preenchido — no render a prop ainda é
 * `undefined` e o alerta sairia sem mensagem.
 *
 * Dependência `[error]` (e não `[]`): a Error Boundary renderiza uma árvore
 * nova a cada erro, então o `Error` é um objeto distinto e o efeito reexecuta.
 * Ver também o alerta de anti-padrão de SSE no AGENTS.md — mesma raiz: não
 * colocar referência de callback volátil em array de deps.
 */
export function useErrorReport({ error, errorInfo, digest, context }: HookOptions): void {
  useEffect(() => {
    if (!error) return;

    sendClientErrorReport({
      message: error.message || String(error),
      stack: error.stack,
      componentStack: errorInfo?.componentStack ?? undefined,
      digest,
      context,
    });
    // `context` e `errorInfo` são estáveis por render da boundary; incluí-los
    // re-reportaria o mesmo erro a cada StrictMode double-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);
}
